import React from 'react';
import EnemyRosterRail from '../Combat/EnemyRosterRail';
import { enemyTooltipSections } from '../GameTip/decode';
import { decodeForEnemy } from '../GameTip/announceDecode';
import CombatTooltip, { CombatTooltipPanel } from '../GameTip/CombatTooltip';
import { CREATURES } from '../../data/combatData';
import { HTN_DRUG_META, htnAvBlockPartnerCopy, normalizeDrugSlug } from '../../utils/htnBlister';
import { htnPulseTooltipProps } from '../CombatPlayerbar/PlayerHtnPulseTag';
import { htnPotassiumTooltipProps } from '../CombatPlayerbar/PlayerPotassiumTag';
import { htnDiuresisTooltipProps } from '../CombatPlayerbar/DiuresisTag';
import { htnVasodilationTooltipProps } from '../CombatPlayerbar/PlayerVasodilationTag';
import {
  DiuresisPill,
  VasodilationPill,
  HeartRatePill,
  PotassiumTag,
} from '../StatusTags/KcStatusPills';
import CanonicalEffectChip from './CanonicalEffectChip';
import './PillCard.css';
import '../CombatPlayerbar/CombatPlayerbar.css';
import '../CombatStatusRails/CombatStatusRails.css';

// Authored HTN starter drug-toast rows — a faithful port of the authored
// reference (wiki/campaigns/debono-stat-model/ref-toasts/antihtn-authored-
// 2026-07-19.html). The row grammar previews the real combat-HUD status tags:
// the potassium K continuum pill, the HR heartbeat pill and the diuresis
// droplet pill are rendered as the reference's `kc-pill` family, the regen
// chip reuses the live `bls-status-header--hp` chrome, and the weaken rows
// render the real enemy-roster minis. Class names for the three kc-pills are
// deliberately kc-pill--<slug> (NOT bls-status-header--<slug>). Those names
// began as an escape hatch from the CombatPlayerbar's former 226px potassium
// meter and now form the shared compact grammar used by both surfaces.

// TOOLTIP PARITY (Edward 2026-07-25). The status pills embedded in a drug toast row
// used to carry reconstructions of their combat tooltips: the K⁺ pill had a bespoke
// two-line `.kc-panel`, and the HR + Diuresis pills had NO hover at all. The
// authoritative truth for these is how the same status reads on hover in the combat
// enemy/player status rail, so each row now mounts THAT rail panel, built by the
// rail component's own exported builder. One definition, two mounts, no drift.
//
// The rail panels are regimen-wide; a toast describes ONE drug, so each is fed a
// single-drug "loadout" and the drug's own authored shift. Everything the panels then
// state (question count, per-strike dispense, drain, the Shifted-by ledger) derives
// from the engine's own tables. Potassium is the one exception and is rendered
// `bandless`: its band/serum/probability come from the whole regimen resolved at
// combat install, so a lone drug cannot determine them and the panel says less
// instead of inventing a band.
function singleDrugLoadout(drugSlug) {
  return drugSlug ? [{ drug_slug: drugSlug }] : [];
}

function htnShift(drugSlug, key) {
  const meta = drugSlug ? HTN_DRUG_META[drugSlug] : null;
  const value = Number(meta?.[key] || 0);
  return Number.isFinite(value) ? value : 0;
}

const WARDEN_BY_SLUG = Object.fromEntries(
  CREATURES
    .filter((enemy) => [
      'raas_warden',
      'calcium_gate',
      'volume_warden',
      'aldosterone_lock',
      'adrenergic_sentry',
    ].includes(enemy.class))
    .map((enemy) => [enemy.class, enemy]),
);

// Reference inline glyphs (exact paths lifted from the authored HTML).
//
// DelayClock is the SAME clock face the `round` / `rounds` status word renders
// (statusWordGlyphs.jsx `delayedIcon`, GameTip/glyphs.jsx `ClockIcon`, and the
// Latanz delayed-damage tag all draw this identical path), because all of them
// mean the same thing: this lands later. It is drawn here rather than imported
// so it matches the file's existing inline-glyph idiom and inherits the
// parenthetical's own colour through `currentColor`, keeping the qualifier one
// visual unit instead of dropping a teal mark inside a rose phrase.
const DelayClock = () => (
  <svg
    className="kc-qual__glyph"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5v5l3.2 2.2" />
  </svg>
);

const CompensatoryArrow = () => (
  <svg
    className="kc-qual__glyph"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 17a8 8 0 0 1 8-8h6" />
    <path d="M15 6l4 3-4 3" />
  </svg>
);

// The real enemy-roster mini bar + its attack-decode hover. Static preview:
// no onSelect (inert), no projected next-attack damage (runtime-only, omitted
// per Edward 2026-07-19). `interactive` (Edward 2026-07-21): the drug TOAST keeps
// the enemy-mini's own hover sub-tooltip; the small combat-rail tooltips render
// the SAME mini but pass interactive={false} so the mini carries NO nested
// sub-tooltip (a hover-tooltip inside a hover-tooltip — the rail surface can't
// accommodate reaching it). Same visual bar, just inert.
function WardenMini({ slug, interactive = true }) {
  const enemy = WARDEN_BY_SLUG[slug];
  if (!enemy) return null;
  const maxHp = Math.max(1, Number(enemy.boss_hp || 1));
  const decode = decodeForEnemy({ ...enemy, slug: enemy.class });
  const sections = enemyTooltipSections(decode, enemy.description);
  return (
    <EnemyRosterRail
      className="pill-card__warden-mini"
      embedded
      enemies={[{
        id: enemy.class,
        slug: enemy.class,
        name: enemy.name,
        tag: 'MINI',
        hp: maxHp,
        maxHp,
        isBoss: false,
        isTarget: false,
        isDead: false,
        isInvuln: false,
        isSpawned: false,
        isAttacking: false,
        clickable: false,
        color: enemy.color,
        tooltip: interactive ? (
          <CombatTooltipPanel
            placement="below-left"
            name={enemy.name}
            subtitle={decode?.entity}
            sections={sections}
          />
        ) : null,
      }]}
    />
  );
}

function WeakenRow({ row, interactive = true }) {
  const strong = row.strength === 'strong';
  return (
    <div
      className="pill-card__influence-row pill-card__influence-row--kc"
      data-influence-row={`${row.strength}-weaken`}
    >
      <span className={`pill-card__influence-verb pill-card__influence-verb--${strong ? 'strong' : 'weak'}`}>
        {strong ? 'Strongly weakens' : 'Weakens'}
      </span>
      <div className="pill-card__hover-tags pill-card__hover-tags--ledger ec-scope">
        <WardenMini slug={row.warden} interactive={interactive} />
      </div>
    </div>
  );
}

// Adherence -> the "Good Outcome" heal. Edward 2026-07-20: HP is not a player
// status chip here; the regen row reads as adherence landing the Good Outcome.
// Render the SAME chassis as the in-combat Good Outcome result pill
// (.batch-result-pill--good-outcome) via CanonicalEffectChip tone="good" (its
// default accent IS metformin-teal, the Good Outcome color). Word "Adherence",
// the +HP magnitude + cadence revealed on hover like every other ledger chip.
function RegenRow({ row, interactive = true }) {
  return (
    <div className="pill-card__influence-row pill-card__influence-row--kc" data-influence-row="regen">
      <span className="pill-card__influence-verb pill-card__influence-verb--regen">Regenerates</span>
      <span className="gt-inflicts__r">
        {/* Toast: hover reveals the +HP magnitude. Rail (interactive=false): show
            it inline via the signature chip so there's no nested hover popover.
            The magnitude and cadence, nothing else (Edward 2026-07-26: "it doesnt
            need to say up to max, just the number of the heal per turn"). The
            ceiling belongs to the combat chip, which states it when it actually
            truncates a heal; restating it here made every card carry a caveat for
            a case most of them never hit. */}
        <CanonicalEffectChip
          tone="good"
          word="Adherence"
          detail={`+${row.amount} HP / ${row.cadence}`}
          signature={!interactive}
        />
      </span>
    </div>
  );
}

// The K continuum pill: bold blue "K+" glyph + a green-gradient monospace
// range readout, with the mechanic explainer in a hover panel. The range is
// authored (HTN_TOOLTIP_POTASSIUM_RANGE, identical for every drug — the pill
// does not encode the drug's push direction, per the reference).
function PotassiumRow({ row, interactive = true, drugSlug }) {
  const parts = String(row.range || '').split(/\s*[–-]\s*/);
  const readout = parts.length === 2
    ? (
      <>
        {parts[0]}
        <span className="kc-readout__dash">&#8211;</span>
        {parts[1]}
      </>
    )
    : row.range;
  // The pill keeps its own `.kc-tag` chrome either way; `interactive={false}` on it
  // only suppresses the retired bespoke panel, so the pill does not move.
  const pill = <PotassiumTag readout={readout} interactive={false} />;
  return (
    <div className="pill-card__influence-row pill-card__influence-row--kc" data-influence-row="potassium">
      <span className="pill-card__influence-verb pill-card__influence-verb--influences">Influences</span>
      {interactive ? (
        <CombatTooltip
          {...htnPotassiumTooltipProps({
            bandless: true,
            net: htnShift(drugSlug, 'k_shift'),
            activeEquippedEffects: singleDrugLoadout(drugSlug),
          })}
        >
          {pill}
        </CombatTooltip>
      ) : pill}
    </div>
  );
}

function PulseRow({ row, interactive = true, drugSlug }) {
  // The (↗ …) qualifier parenthetical is DEDICATED to the game-layer
  // "compensatory" reflex (DHP CCBs, pulse_shift > 0). Direct rate-control
  // actions (beta blockers + non-DHP CCBs, pulse_shift < 0 → 'rate control')
  // must NOT borrow the CompensatoryArrow element — they render the bare HR
  // pill with no parenthetical. Gate on the qualifier so the reservation holds
  // even if htnBlister keeps emitting a non-compensatory qualifier string.
  const isCompensatory = row.qualifier === 'compensatory';
  // Rounds the mechanic waits before it starts, authored beside the shift itself
  // (htnBlister HTN_MECHANIC_DELAY_ROUNDS, mirroring the backend's
  // `activates_at_round`). It rides INSIDE the parenthetical, immediately after
  // the qualifier word, per Edward 2026-07-28: the delay is a property of the
  // compensatory reflex, not a separate claim sitting beside it. 0 renders
  // nothing, so every rate-control drug's row is unchanged.
  const delayRounds = Number(row.delayRounds || 0);
  return (
    <div className="pill-card__influence-row pill-card__influence-row--kc" data-influence-row="pulse">
      <span className="pill-card__influence-verb pill-card__influence-verb--influences">
        Influences
        {isCompensatory && (
          <span className="kc-qual">
            {'('}
            <span className="kc-qual__inner kc-qual__inner--hr">
              <CompensatoryArrow />
              <span>{row.qualifier}</span>
              {delayRounds > 0 && (
                <>
                  <DelayClock />
                  <span className="kc-qual__num">{delayRounds}</span>
                </>
              )}
            </span>
            {')'}
          </span>
        )}
      </span>
      {interactive ? (
        <CombatTooltip
          {...htnPulseTooltipProps({
            net: htnShift(drugSlug, 'pulse_shift'),
            activeEquippedEffects: singleDrugLoadout(drugSlug),
            // Draft-time caution on every beta blocker and both non-DHP CCBs. A
            // single card can never BE the block, so this is the only place the
            // pair rule can reach the player before it costs them the round.
            avBlockWarning: htnAvBlockPartnerCopy(drugSlug),
          })}
        >
          <HeartRatePill />
        </CombatTooltip>
      ) : <HeartRatePill />}
    </div>
  );
}

// Bare verb plus the Diuresis tag, matching the potassium row. The cadence word and
// the (N) qualifier were dropped 2026-07-26 (Edward: "Inflicts ... [Diuresis tag] and
// thats it"): the row's job is to say WHICH status this drug inflicts, and the tag
// itself is the thing that carries the magnitude. The number is still resolved here
// because the hover tooltip states the drug's own drain; it is simply not printed in
// the row.
function DiuresisRow({ row, interactive = true, drugSlug }) {
  const atCombatStart = row.atCombatStart != null && Number.isFinite(Number(row.atCombatStart))
    ? Number(row.atCombatStart)
    : null;
  const perRound = row.perRound != null && Number.isFinite(Number(row.perRound))
    ? Number(row.perRound)
    : null;
  const amount = atCombatStart === null ? perRound : atCombatStart;
  return (
    <div className="pill-card__influence-row pill-card__influence-row--kc" data-influence-row="diuresis">
      <span className="pill-card__influence-verb pill-card__influence-verb--influences">
        Inflicts
      </span>
      {interactive ? (
        <CombatTooltip
          {...htnDiuresisTooltipProps({
            drain: amount,
            // One diuretic covers exactly one nephron segment; the rail's synergy line
            // then correctly reads "cover a different segment for a bonus".
            segments: 1,
            activeEquippedEffects: singleDrugLoadout(drugSlug),
          })}
        >
          <DiuresisPill />
        </CombatTooltip>
      ) : <DiuresisPill />}
    </div>
  );
}

// "Inflicts (remodel 2) [Remodel 5 to 20 / round]" — the beta class's reverse cardiac
// remodelling, an extra flat hit that opens on round 3 and doubles until it caps.
//
// Same parenthetical grammar as the compensatory pulse (Edward 2026-07-28): the
// mechanic word, then the clock and the rounds it STILL has to wait, all inside one
// parenthetical. The delay is a property of the hit, not a separate claim beside it.
//
// The chip carries the effect's own name because that is the exact word the combat
// interaction labels the strike with. A player who reads "Remodel" here sees "Remodel"
// land, which is the whole point of naming a delayed mechanic on the card at all.
function RemodelRow({ row, interactive = true }) {
  const delayRounds = Number(row.delayRounds || 0);
  const opening = Math.max(0, Math.trunc(Number(row.opening) || 0));
  const max = Math.max(0, Math.trunc(Number(row.max) || 0));
  return (
    <div className="pill-card__influence-row pill-card__influence-row--kc" data-influence-row="remodel">
      <span className="pill-card__influence-verb pill-card__influence-verb--influences">
        Inflicts
        <span className="kc-qual">
          {'('}
          <span className="kc-qual__inner kc-qual__inner--remodel">
            <span>remodel</span>
            {delayRounds > 0 && (
              <>
                <DelayClock />
                <span className="kc-qual__num">{delayRounds}</span>
              </>
            )}
          </span>
          {')'}
        </span>
      </span>
      <span className="gt-inflicts__r">
        <CanonicalEffectChip
          tone="pos"
          word="Remodel"
          detail={`${opening} to ${max} / round`}
          signature={!interactive}
        />
      </span>
    </div>
  );
}

// "Regenerates (mortality 2) [Mortality Benefit +5 HP / round]" — the player-side heal
// the three HFrEF beta blockers carry from round 3.
//
// The verb is Regenerates, the same as Adherence, because it IS the same kind of claim:
// a named per-round heal. The two can never appear on one card (Adherence is ACE / ARB /
// DHP CCB), so nothing here has to disambiguate two heal rows.
//
// It carries the delay parenthetical for the same reason Remodel does: a mechanic that
// does nothing for two rounds and never says so is exactly the gap the delayed-activation
// primitive exists to close.
function MortalityBenefitRow({ row, interactive = true }) {
  const delayRounds = Number(row.delayRounds || 0);
  return (
    <div
      className="pill-card__influence-row pill-card__influence-row--kc"
      data-influence-row="mortality-benefit"
    >
      <span className="pill-card__influence-verb pill-card__influence-verb--regen">
        Regenerates
        <span className="kc-qual">
          {'('}
          <span className="kc-qual__inner kc-qual__inner--mortality">
            <span>mortality</span>
            {delayRounds > 0 && (
              <>
                <DelayClock />
                <span className="kc-qual__num">{delayRounds}</span>
              </>
            )}
          </span>
          {')'}
        </span>
      </span>
      <span className="gt-inflicts__r">
        <CanonicalEffectChip
          tone="good"
          word="Mortality Benefit"
          detail={`+${row.amount} HP / ${row.cadence}`}
          signature={!interactive}
        />
      </span>
    </div>
  );
}

// "Grants [Vasodilation 3]" — the defensive coupon a vasodilating card hands the
// player at combat open. The verb is GRANTS, not Inflicts or Influences: this is the
// only HTN row that gives the player a resource rather than describing something the
// card does to a body or an enemy. The count rides IN the tag, the same way the
// Diuresis row leaves its magnitude to its own tag, because the number here is the
// resource itself and not a side note about it.
function GrantsVasodilationRow({ row, interactive = true, drugSlug }) {
  const charges = Math.max(0, Math.trunc(Number(row.charges) || 0));
  const drugName = HTN_DRUG_META[drugSlug]?.name;
  const pill = <VasodilationPill charges={charges} />;
  return (
    <div className="pill-card__influence-row pill-card__influence-row--kc" data-influence-row="vasodilation">
      <span className="pill-card__influence-verb pill-card__influence-verb--influences">
        Grants
      </span>
      {interactive ? (
        <CombatTooltip
          {...htnVasodilationTooltipProps({
            initial: charges,
            sourceNames: drugName ? [drugName] : [],
          })}
        >
          {pill}
        </CombatTooltip>
      ) : pill}
    </div>
  );
}

// Single canonical renderer shared by the drug toast and the HTN player-rail
// source groups. Keeping the switch here makes a selected row byte-identical on
// both surfaces; callers own grouping and keys so multiple drugs may safely emit
// the same row.type without sibling-key collisions.
// `interactive` (default true) is threaded to the rows whose embedded element
// carries its OWN hover sub-tooltip (weaken → WardenMini, regen → CanonicalEffectChip,
// potassium → PotassiumTag). The drug TOAST renders interactive; the compact combat-
// rail tooltips pass interactive={false} so those nested sub-tooltips are suppressed
// (same visual rows, no tooltip-inside-a-tooltip). Diuresis / pulse pills are bare
// (no sub-tooltip) so they ignore the flag.
export function DrugInfluenceRow({ row, interactive = true, drugSlug }) {
  if (row?.type === 'inflicts' && row.status === 'diuresis') {
    return <DiuresisRow row={row} interactive={interactive} drugSlug={drugSlug} />;
  }
  if (row?.type === 'inflicts' && row.status === 'remodel') {
    return <RemodelRow row={row} interactive={interactive} />;
  }
  if (row?.type === 'mortality-benefit') {
    return <MortalityBenefitRow row={row} interactive={interactive} />;
  }
  if (row?.type === 'regen') {
    return <RegenRow row={row} interactive={interactive} />;
  }
  if (row?.type === 'weaken') {
    return <WeakenRow row={row} interactive={interactive} />;
  }
  if (row?.type === 'influences-potassium') {
    return <PotassiumRow row={row} interactive={interactive} drugSlug={drugSlug} />;
  }
  if (row?.type === 'influences-pulse') {
    return <PulseRow row={row} interactive={interactive} drugSlug={drugSlug} />;
  }
  if (row?.type === 'grants' && row.status === 'vasodilation') {
    return <GrantsVasodilationRow row={row} interactive={interactive} drugSlug={drugSlug} />;
  }
  return null;
}

// CombatTooltip cannot derive accessible text from pre-rendered section.content,
// so the rail uses this serializer to build its explicit full aria-label from the
// exact same row model the visual renderer consumes.
export function drugInfluenceRowText(row) {
  if (row?.type === 'inflicts' && row.status === 'diuresis') {
    // Matches the visual row exactly: no cadence, no amount. The magnitude lives in
    // the Diuresis tag's own tooltip, so printing it here would be a second, drifting
    // copy of it.
    return 'Inflicts Diuresis';
  }
  if (row?.type === 'inflicts' && row.status === 'remodel') {
    // Same treatment as the compensatory pulse: the clock and its numeral are
    // decorative to a screen reader, so the delay is spelled out in the parenthetical
    // position the visual row draws it in.
    const delayRounds = Number(row.delayRounds || 0);
    const schedule = `Remodel ${Math.max(0, Math.trunc(Number(row.opening) || 0))} to ${Math.max(0, Math.trunc(Number(row.max) || 0))} per round`;
    return delayRounds > 0
      ? `Inflicts (remodel, starts after ${delayRounds} round${delayRounds === 1 ? '' : 's'}) ${schedule}`
      : `Inflicts (remodel) ${schedule}`;
  }
  if (row?.type === 'mortality-benefit') {
    // Same treatment as the other delayed rows: the clock is decorative to a screen
    // reader, so the wait is spelled out in the parenthetical position it is drawn in.
    const delayRounds = Number(row.delayRounds || 0);
    const heal = `Mortality Benefit +${row.amount} HP per ${row.cadence}`;
    return delayRounds > 0
      ? `Regenerates (mortality, starts after ${delayRounds} round${delayRounds === 1 ? '' : 's'}) ${heal}`
      : `Regenerates (mortality) ${heal}`;
  }
  if (row?.type === 'regen') {
    return `Regenerates Adherence +${row.amount} HP per ${row.cadence}`;
  }
  if (row?.type === 'weaken') {
    // Resolve the warden slug to its display name so the aria text matches the
    // visual row (WeakenRow renders WARDEN_BY_SLUG[slug].name, not the slug).
    const wardenName = WARDEN_BY_SLUG[row.warden]?.name || row.warden;
    return `${row.strength === 'strong' ? 'Strongly weakens' : 'Weakens'} ${wardenName}`;
  }
  if (row?.type === 'influences-potassium') {
    return `Influences Potassium ${row.range}. ${row.line || ''}`.trim();
  }
  if (row?.type === 'influences-pulse') {
    if (row.qualifier !== 'compensatory') return 'Influences heart rate';
    // The clock and its numeral are decorative to a screen reader, so the delay
    // is spelled out here instead. Kept in the same parenthetical position as
    // the visual row so the two describe the row identically.
    const delayRounds = Number(row.delayRounds || 0);
    return delayRounds > 0
      ? `Influences (compensatory, starts after ${delayRounds} round${delayRounds === 1 ? '' : 's'}) heart rate`
      : 'Influences (compensatory) heart rate';
  }
  if (row?.type === 'grants' && row.status === 'vasodilation') {
    const charges = Math.max(0, Math.trunc(Number(row.charges) || 0));
    return `Grants Vasodilation ${charges} charge${charges === 1 ? '' : 's'}`;
  }
  return '';
}

// `drug` identifies WHICH drug these rows describe (card object or slug, the same
// value the caller passed to `drugInfluenceRows`), so the embedded status pills can
// mount the real combat-rail tooltip for that drug (see the parity note above). It is
// resolved through the SAME normalizeDrugSlug the row builder uses, so a row and its
// tooltip can never end up describing different drugs. Omitting it degrades the pills
// to their bare form rather than showing a wrong drug's numbers.
export default function DrugInfluenceRows({ rows = [], interactive = true, drug }) {
  const valid = Array.isArray(rows) ? rows : [];
  if (!valid.length) return null;
  const drugSlug = drug ? normalizeDrugSlug(drug) : undefined;
  return (
    <div className="pill-card__influence-rows combat-status-rail" data-testid="drug-influence-rows">
      {valid.map((row, index) => {
        const shared = { row, interactive, drugSlug };
        if (row.type === 'inflicts' && (row.status === 'diuresis' || row.status === 'remodel')) {
          return <DrugInfluenceRow key={`${row.type}-${row.status}`} {...shared} />;
        }
        if (row.type === 'mortality-benefit') {
          return <DrugInfluenceRow key={row.type} {...shared} />;
        }
        if (row.type === 'regen') {
          return <DrugInfluenceRow key={`${row.type}-${index}`} {...shared} />;
        }
        if (row.type === 'weaken') {
          return <DrugInfluenceRow key={`${row.strength}-${row.warden}`} {...shared} />;
        }
        if (row.type === 'influences-potassium') {
          return <DrugInfluenceRow key={row.type} {...shared} />;
        }
        if (row.type === 'influences-pulse') {
          return <DrugInfluenceRow key={row.type} {...shared} />;
        }
        if (row.type === 'grants' && row.status === 'vasodilation') {
          return <DrugInfluenceRow key={`${row.type}-${row.status}`} {...shared} />;
        }
        return null;
      })}
    </div>
  );
}
